import { createContext, useContext, useEffect, useRef, useState } from 'react';

// From https://github.com/reduxjs/react-redux/blob/3e53ff96ed10f71c21346f08823e503df724db35/src/utils/shallowEqual.js
var hasOwn = Object.prototype.hasOwnProperty;
function is(x, y) {
    if (x === y) {
        return x !== 0 || y !== 0 || 1 / x === 1 / y;
    }
    else {
        return x !== x && y !== y;
    }
}
function shallowEqual(objA, objB) {
    if (is(objA, objB)) {
        return true;
    }
    if (typeof objA !== 'object' ||
        objA === null ||
        typeof objB !== 'object' ||
        objB === null) {
        return false;
    }
    var keysA = Object.keys(objA);
    var keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) {
        return false;
    }
    // tslint:disable-next-line:prefer-for-of
    for (var i = 0; i < keysA.length; i++) {
        if (!hasOwn.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
            return false;
        }
    }
    return true;
}

var Context = createContext(null);
var StoreProvider = Context.Provider;
/**
 * Your passed in mapState function should be memoized to avoid
 * resubscribing every render. If you use other props in mapState, use
 * useCallback to memoize the resulting function, otherwise define the mapState
 * function outside of the component:
 *
 * const mapState = useCallback(
 *   state => state.todos.get(id),
 *   // The second parameter to useCallback tells you
 *   [id],
 * );
 * const todo = useMappedState(mapState);
 */
function useMappedState(mapState) {
    var store = useContext(Context);
    if (!store) {
        throw new Error('redux-react-hook requires your Redux store to be passed through context via the <StoreProvider>');
    }
    var runMapState = function () { return mapState(store.getState()); };
    var _a = useState(function () { return runMapState(); }), mappedState = _a[0], setMappedState = _a[1];
    // If the store or mapState change, rerun mapState
    var _b = useState(store), prevStore = _b[0], setPrevStore = _b[1];
    var _c = useState(function () { return mapState; }), prevMapState = _c[0], setPrevMapState = _c[1];
    if (prevStore !== store || prevMapState !== mapState) {
        setPrevStore(store);
        setPrevMapState(function () { return mapState; });
        setMappedState(runMapState());
    }
    // We use a ref to store the last result of mapState in local component
    // state. This way we can compare with the previous version to know if
    // the component should re-render. Otherwise, we'd have pass mappedState
    // in the array of memoization paramaters to the second useEffect below,
    // which would cause it to unsubscribe and resubscribe from Redux everytime
    // the state changes.
    var lastRenderedMappedState = useRef();
    // Set the last mapped state after rendering.
    useEffect(function () {
        lastRenderedMappedState.current = mappedState;
    });
    useEffect(function () {
        // Run the mapState callback and if the result has changed, make the
        // component re-render with the new state.
        var checkForUpdates = function () {
            var newMappedState = runMapState();
            if (!shallowEqual(newMappedState, lastRenderedMappedState.current)) {
                setMappedState(newMappedState);
            }
        };
        // Pull data from the store on first render.
        checkForUpdates();
        // Subscribe to the store to be notified of subsequent changes.
        var unsubscribe = store.subscribe(checkForUpdates);
        // The return value of useEffect will be called when unmounting, so
        // we use it to unsubscribe from the store.
        return unsubscribe;
    }, [store, mapState]);
    return mappedState;
}
function useDispatch() {
    var store = useContext(Context);
    if (!store) {
        throw new Error('redux-react-hook requires your Redux store to be passed through context via the <StoreProvider>');
    }
    return store.dispatch;
}

export { StoreProvider, useMappedState, useDispatch };
//# sourceMappingURL=index.es.js.map
